import React, { useEffect, useState } from 'react';
import { DragDropContext, Droppable } from 'react-beautiful-dnd';

import useTaskManager from '../useTaskManager';
import '../MyPlannerList.scss';
import { localStorageInstance } from '../../../../utils/localStorage';
import NoteEditor from '../components/NoteEditor';
import MyPlannerListItem from './MyPlannerListItem';
import AddTaskItem from './AddTaskItem';
import OpenCardDetail from '../../../board/components/Kanban/openCardDetail';
import { Typography } from '@mui/material';
import CustomMenu from '../../../../components/Menu';
import CustomMenuItem from '../../../../components/Menu/CustomMenuItem';
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import { useDispatch } from 'react-redux';
import { setTodoTask } from '../../../../redux/plannerFilterSlice';

function MyPlannerList({ filter, filteredTasks, anchorEl, setFilter, handleOpen, handleClose, searchText, inputValue }) {
  const {
    handleToggleTask,
    handleKeyDown,
    handleDeleteTask,
    createTaskFromCard,
    // Notes dialog state and functions
    isNoteDialogOpen,
    currentNote: taskManagerNote,
    handleOpenNotes,
    handleCloseNotes,
    handleSaveNote,
    deleteNote,
    // Loading and error states
    isLoading,
    error,
    // Due date function
    updateDueDate,
    openDetailCard,
    setOpenDetailCard,
    mutateLoading,
  } = useTaskManager();
  const dispatch = useDispatch();

  // Local state to manage the current note for NoteEditor
  const [currentNote, setCurrentNote] = useState(null);

  // Update local currentNote when taskManagerNote changes
  useEffect(() => {
    setCurrentNote(taskManagerNote);
  }, [taskManagerNote?.id]);

  if (isLoading) {
    return <div className="task-planner">Loading tasks...</div>;
  }

  if (error) {
    return <div className="task-planner">Error loading tasks: {error.message}</div>;
  }

  

  // Custom save handler to handle both the local state and taskManager state
  const handleNoteEditorSave = () => {
    if (currentNote) {
      handleSaveNote({
        id: currentNote?.id,
        title: currentNote.title,
        content: currentNote.content,
        ...(currentNote?.color_code
          ? { color_code: currentNote.color_code }
          : {}),
        taskId: currentNote.taskId,
      });
    }
    setCurrentNote(null);
  };

  return (
    <div className="task-planner">
      {openDetailCard && (
        <OpenCardDetail
          cardsOnBoard={filteredTasks
            .filter((task) => task?.source_id)
            .map((task) => {
              return { card: { card_id: task.source_id } };
            }).filter((task) => task?.title?.toLowerCase().includes(inputValue.toLowerCase()))
          }
        />
      )}
      {/* <DragDropContext onDragEnd={(result) => onDragEnd(result, filteredTasks)}> */}
      <div className='my_tasks_list_container'>
        <Typography>My Tasks</Typography>
        <div
          className="filter-select d-flex align-items-center"
          onClick={(e) => {
            e.stopPropagation();
            handleOpen(e);
          }}
        >
          <span>{filter}</span>
          <KeyboardArrowDownIcon className="cursor-pointer" />
        </div>
      </div>
      <Droppable droppableId="left-tasks">
        {(provided) => (
          <div
            className="task-list"
            {...provided.droppableProps}
            ref={provided.innerRef}
          >
            {filteredTasks.length === 0 && (
              <div className="no-tasks-message">
                No tasks found. Add a new task to get started!
              </div>
            )}
            <div className="add-task-container">
              <AddTaskItem
                handleKeyDown={(e) => {
                  // Only pass through keyboard events that might be relevant
                  if (e.key !== 'Enter') {
                    // Handle other keyboard shortcuts if needed
                  }
                }}
              />
            </div>
            {filteredTasks?.map((task, index) => (
              <MyPlannerListItem
                key={task.id}
                task={task}
                index={index}
                handleDeleteTask={handleDeleteTask}
                handleToggleTask={handleToggleTask}
                handleOpenNotes={handleOpenNotes}
                updateDueDate={updateDueDate}
                setOpenDetailCard={setOpenDetailCard}
                filter={filter}
              />
            ))}
            {provided.placeholder}
          </div>
        )}
      </Droppable>
      {/* </DragDropContext> */}

      {/* Note Editor */}
      {isNoteDialogOpen && <NoteEditor
        open={isNoteDialogOpen}
        onClose={() => {
          if (currentNote) {
            handleCloseNotes({
              id: currentNote?.id,
              title: currentNote.title,
              content: currentNote.content,
              ...(currentNote?.color_code
                ? { color_code: currentNote.color_code }
                : {}),
              taskId: currentNote.taskId,
            });
            setCurrentNote(null);
          } else {
            handleCloseNotes();
          }
        }}
        onSave={handleNoteEditorSave}
        onDelete={() => {
          if (currentNote?.id) {
            deleteNote(currentNote.id);
          } else {
            setCurrentNote(null);
            handleCloseNotes();
          }
        }}
        noteData={{
          id: currentNote?.id || null,
          title: currentNote?.title || "",
          content: currentNote?.content || "",
          color_code: currentNote?.color_code || "", // Default yellow
          created_at: currentNote?.created_at || "",
        }}
        onNoteChange={(updatedNote) => {
          setCurrentNote({
            ...currentNote,
            id: updatedNote.id,
            title: updatedNote.title,
            content: updatedNote.content,
            color_code: updatedNote.color_code,
            date: updatedNote.created_at,
          });
        }}
        isLoading={mutateLoading}
        width="591px"
      />}

      <CustomMenu
        anchorEl={anchorEl}
        open={!!anchorEl}
        handleClose={handleClose}
      >
        {[
          {
            label: "All",
            value: "All",
          },
          {
            label: "Mine",
            value: "Mine",
          },
          {
            label: "Assigned to me",
            value: "Assigned to me",
          },
          {
            label: "Completed",
            value: "Completed",
          },
        ].map((item) => (
          <CustomMenuItem
            onClick={(event) => {
              event.stopPropagation();
              setFilter(item.value);
              dispatch(setTodoTask(item.value));
              handleClose();
            }}
          >
            <label className="custom-checkbox">
              <input
                type="checkbox"
                id={`board-list-my-task-checkbox-all-board`}
                checked={filter === item.value}
              />
              <span className="checkmark"></span>
              {item.label}
            </label>
          </CustomMenuItem>
        ))}
      </CustomMenu>
    </div>
  );
}

export default MyPlannerList;